import { debounce, get, set, unset } from 'lodash';
import { useCallback } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Divider } from '@hivebrite/design-system/Divider';

import CheckboxSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/CheckboxSetting';
import ColorSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ColorSetting';
import { ColorPickerProvider } from 'hb-builder/contexts';
import type { Config } from 'hb-builder/types';
import type { OldBuilderActionsHeaderProps } from 'hb-react/backoffice/components/BuilderWrapper/components/Actions/OldBuilderActionsHeader';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';

import BackgroundColorMode from './BackgroundColorMode';
import DownloadDocument from './DownloadDocument';
import DownloadNetworkConfig from './DownloadNetworkConfig';
import Upload from './Upload';
import Width, { type Updater } from './Width';

const updaters: Updater[] = [];

interface BodySettingsProps {
  bodyItem: DocumentItem;
  config: Config;
  fullDocument: DocumentItem;
  getPath: (id: string) => string; // TODO: update types here when BuilderContext is typed (same for uploadDocument & updateItems)
  onClose: () => void;
  uploadDocument: OldBuilderActionsHeaderProps['saveDocument'];
  updateItems: (args: { path: string; updaters: Updater[] }) => DocumentItem;
}

const BodySettings = ({
  bodyItem,
  config,
  fullDocument,
  getPath,
  onClose,
  uploadDocument,
  updateItems,
}: BodySettingsProps) => {
  const {
    features: {
      body: {
        canBeFullWidth,
        canChangeWidth,
        canInheritNetworkBgColor,
        defaultWidth,
      },
      color,
      footer: { displayFooter: canDisplayFooter } = {},
    },
    hivebrite: { ids: authIds, networkColors, networkTheming, permissions },
  } = config;

  const handleChange = debounce(({ value, updater }) => {
    updaters.push(() => updater({ item: bodyItem, value }));
  }, 150);

  const saveChanges = () => {
    updateItems({ path: getPath(bodyItem.id), updaters });
    onClose();
  };

  const onChange = useCallback(
    ({ settingKey, value }: { settingKey?: string; value?: any }) => {
      if (!settingKey) return;

      return handleChange({
        value,
        updater: ({ item, value }) => {
          if (value) {
            set(item, settingKey, value);
          } else if (get(item, settingKey)) {
            unset(item, settingKey);
          }

          return item;
        },
      });
    },
    [handleChange],
  );

  const getColorSetting = () => {
    if (!networkColors) return null;

    return canInheritNetworkBgColor ? (
      <BackgroundColorMode
        color={color}
        item={bodyItem}
        networkColors={networkColors}
        networkTheming={networkTheming}
        onChange={onChange}
      />
    ) : (
      <ColorSetting
        color={color}
        networkColors={networkColors}
        networkTheming={networkTheming}
        settingKey="bodyBackgroundColor"
        style={{ position: 'absolute', right: '70px', top: '-10px' }}
        title={__('Body background color')}
        {...settingProps}
      />
    );
  };

  const settingProps = { item: bodyItem, onChange };

  return (
    <ColorPickerProvider>
      <Wrapper>
        {getColorSetting()}
        {canChangeWidth && (
          <>
            <Divider my={4} />
            <Width
              canBeFullWidth={canBeFullWidth}
              defaultWidth={defaultWidth}
              item={bodyItem}
              onChange={handleChange}
            />
          </>
        )}
        {canDisplayFooter && (
          <>
            <Divider mb={4} mt={0} />
            <CheckboxSetting
              settingKey="displayFooter"
              title={__('Display footer')}
              {...settingProps}
            />
          </>
        )}
        {permissions?.canDebug && (
          <>
            <Divider mb={4} mt={0} />
            <DownloadNetworkConfig config={config} />
            <Divider mb={4} mt={0} />
            <DownloadDocument authIds={authIds} fullDocument={fullDocument} />
            <Divider mb={4} mt={0} />
            <Upload authIds={authIds} uploadDocument={uploadDocument} />
          </>
        )}
        <Box justifyContent="flex-end" mt={4} width="100%">
          <Button fontFamily="Inter" onClick={saveChanges} variant="primary">
            {__('Save')}
          </Button>
        </Box>
      </Wrapper>
    </ColorPickerProvider>
  );
};

export default BodySettings;

const Wrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  width: '100%',
}))``;
